import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { freelancerLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefHTML } from '../../lib/brief.js';
import { BRIEF_STYLES } from '../../lib/brief-styles.js';
import { loadConfig } from '../../lib/config.js';
import {
    downloadMarkdown,
    formatCents,
    formatMediumDate,
    sizeLabel,
    versionLabel,
} from '../../lib/format.js';
import { router } from '../../lib/router.js';
import { commissionTemplateLinks } from '../../lib/templates.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';
import '../shared/upload-progress.js';
import './sow-view.js';

const STATUS_COLORS = {
    open: 'bg-primary',
    assigned: 'bg-info',
    completed: 'bg-success',
    archived: 'bg-secondary',
};

class CommissionsView extends BaseComponent {
    static properties = {
        _commissions: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _statusFilter: { type: String, state: true },
        _view: { type: String, state: true },
        _selected: { type: Object, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _dragOver: { type: Boolean, state: true },
        _lightboxSrc: { type: String, state: true },
        _modal: { type: String, state: true }, // null, 'style-guide', 'technical-spec', 'assign'
        _freelancers: { type: Array, state: true },
        _assignFreelancerId: { type: String, state: true },
        _assigning: { type: Boolean, state: true },
        _withdrawing: { type: Boolean, state: true },
        _config: { type: Object, state: true },
    };

    static styles = [
        BRIEF_STYLES,
        css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .detail-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .form-panel { max-width: 600px; }
        .brief-text { font-size: 0.9rem; }
        .spec-text { white-space: pre-wrap; font-size: 0.9rem; line-height: 1.6; }
        .status-badge { font-size: 0.75rem; }
        .drop-zone {
            border: 2px dashed #ccc;
            border-radius: 8px;
            padding: 2rem;
            text-align: center;
            cursor: pointer;
            transition: border-color 0.15s, background-color 0.15s;
        }
        .drop-zone:hover, .drop-zone.drag-over {
            border-color: #0d6efd;
            background-color: #f0f6ff;
        }
        .drop-zone input[type="file"] {
            position: absolute;
            width: 0;
            height: 0;
            opacity: 0;
        }
        .spec-link {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 0.75rem;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            text-decoration: none;
            color: #333;
            font-size: 0.9rem;
        }
        .spec-link:hover { background: #f8f9fa; border-color: #adb5bd; }
        .spec-link i { color: #666; }
        .detail-layout { display: flex; gap: 2rem; }
        .detail-main { flex: 1; min-width: 0; }
        .detail-sidebar { width: 280px; flex-shrink: 0; }
        .ref-images-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .ref-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .ref-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; }
        .ref-thumb img:hover { opacity: 0.8; }
        .ref-thumb-info {
            padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666;
            display: flex; justify-content: space-between; align-items: center;
        }
        .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9998;
        }
        .modal-panel {
            background: white;
            border-radius: 8px;
            width: 90vw;
            max-width: 700px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 1rem 3rem rgba(0,0,0,0.2);
        }
        .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid #dee2e6;
        }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body {
            padding: 1.25rem;
            overflow-y: auto;
            flex: 1;
        }
        .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            padding: 0.75rem 1.25rem;
            border-top: 1px solid #dee2e6;
        }
    `,
    ];

    constructor() {
        super();
        this._commissions = [];
        this._loading = true;
        this._error = null;
        this._statusFilter = 'hide-archived';
        this._view = 'list';
        this._selected = null;
        this._formData = {};
        this._saving = false;
        this._dragOver = false;
        this._lightboxSrc = '';
        this._modal = null;
        this._freelancers = [];
        this._assignFreelancerId = '';
        this._assigning = false;
        this._withdrawing = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
        loadConfig()
            .then((cfg) => {
                this._config = cfg;
            })
            .catch(() => {});
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path === '/commissions/new') {
            // A commission is made on a spec, in the art pipeline (#195).
            router.navigate('/pipeline');
            return;
        } else if (path.match(/^\/commissions\/[^/]+\/edit$/)) {
            const id = router.params.id;
            if (!this._selected || this._selected.id !== id) {
                await this._loadDetail(id);
            }
            if (this._selected) {
                this._view = 'edit';
                this._formData = {
                    asset_type: this._selected.asset_type,
                    slug: this._selected.slug,
                    brief: this._selected.brief,
                    rate:
                        this._selected.rate_cents != null
                            ? (this._selected.rate_cents / 100).toFixed(2)
                            : '',
                    miniature_size: this._selected.miniature_size || '',
                    miniature_type: this._selected.miniature_type || '',
                    priority: this._selected.priority || 'medium',
                    claimable: !!this._selected.claimable,
                };
                this._error = null;
            }
        } else if (path.match(/^\/commissions\/[^/]+$/) && router.params.id) {
            await this._loadDetail(router.params.id);
            this._view = 'detail';
        } else {
            this._view = 'list';
            this._selected = null;
            this._loadCommissions();
        }
    }

    async _loadCommissions() {
        this._loading = true;
        this._error = null;
        try {
            const params = new URLSearchParams();
            if (this._statusFilter && this._statusFilter !== 'hide-archived') {
                params.set('status', this._statusFilter);
            }
            const data = await api.get(`/api/commissions?${params}`);
            let results = data.commissions || [];
            if (this._statusFilter === 'hide-archived') {
                results = results.filter((c) => c.status !== 'archived');
            }
            this._commissions = results;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _loadDetail(id) {
        this._error = null;
        try {
            this._selected = await api.get(`/api/commissions/${id}`);
        } catch (err) {
            this._error = err.message;
        }
    }

    _openDetail(commission) {
        router.navigate(`/commissions/${commission.id}`);
    }

    _backToList() {
        router.navigate('/commissions');
    }

    _startEdit() {
        router.navigate(`/commissions/${this._selected.id}/edit`);
    }

    async _handleRefUploadComplete(e) {
        const { upload_id } = e.detail;
        this._error = null;
        try {
            const form = new FormData();
            form.append('upload_id', upload_id);
            await api.upload(`/api/commissions/${this._selected.id}/reference-images`, form);
            this._selected = await api.get(`/api/commissions/${this._selected.id}`);
            const uploader = /** @type {any} */ (this.shadowRoot.querySelector('upload-progress'));
            if (uploader) uploader.reset();
        } catch (err) {
            this._error = err.message;
        }
    }

    async _removeReferenceImage(imageId) {
        this._error = null;
        try {
            await api.delete(`/api/commissions/${this._selected.id}/reference-images/${imageId}`);
            this._selected = await api.get(`/api/commissions/${this._selected.id}`);
        } catch (err) {
            this._error = err.message;
        }
    }

    async _submitEdit(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const body = {
                slug: this._formData.slug,
                brief: this._formData.brief,
                priority: this._formData.priority,
            };
            if (this._formData.rate !== '') {
                body.rate_cents = Math.round(parseFloat(this._formData.rate) * 100);
            }
            if (this._formData.miniature_size) {
                body.miniature_size = this._formData.miniature_size;
            }
            if (this._formData.miniature_type) {
                body.miniature_type = this._formData.miniature_type;
            }
            body.claimable = !!this._formData.claimable;
            this._selected = await api.put(`/api/commissions/${this._selected.id}`, body);
            router.navigate(`/commissions/${this._selected.id}`);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _archiveCommission() {
        this._error = null;
        try {
            this._selected = await api.put(`/api/commissions/${this._selected.id}/status`, {
                status: 'archived',
            });
            this._loadCommissions();
        } catch (err) {
            this._error = err.message;
        }
    }

    async _reopenCommission() {
        this._error = null;
        try {
            this._selected = await api.put(`/api/commissions/${this._selected.id}/status`, {
                status: 'open',
            });
            this._loadCommissions();
        } catch (err) {
            this._error = err.message;
        }
    }

    _handleStatusFilter(e) {
        this._statusFilter = e.target.value;
        this._loadCommissions();
    }

    async _openAssignModal() {
        this._error = null;
        this._assignFreelancerId = '';
        this._modal = 'assign';
        try {
            const data = await api.get('/api/freelancers?include_staff=true');
            this._freelancers = data.freelancers || [];
        } catch (err) {
            this._error = err.message;
        }
    }

    async _assignCommission() {
        if (!this._assignFreelancerId || !this._selected) return;
        this._assigning = true;
        this._error = null;
        try {
            this._selected = await api.post(`/api/commissions/${this._selected.id}/assign`, {
                user_id: this._assignFreelancerId,
            });
            this._modal = null;
            this._loadCommissions();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._assigning = false;
        }
    }

    // Withdrawing takes back an offer nobody countersigned, and voids whatever
    // SOW was raised for it — the commission goes back to plain 'open' and can be
    // offered to somebody else.
    async _withdrawOffer() {
        if (!this._selected) return;
        this._withdrawing = true;
        this._error = null;
        try {
            this._selected = await api.post(
                `/api/commissions/${this._selected.id}/withdraw-offer`,
                {},
            );
            this._loadCommissions();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._withdrawing = false;
        }
    }

    _priorityBadge(priority) {
        const colors = {
            urgent: 'bg-danger',
            high: 'bg-warning text-dark',
            medium: 'bg-secondary',
            low: 'bg-light text-dark',
        };
        return html`<span class="badge status-badge ${colors[priority] || 'bg-secondary'}">${priority}</span>`;
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>Commissions</h4>
                <button class="btn btn-outline-primary btn-sm" title="A commission is made on a spec in the Asset Pipeline"
                    @click="${() => router.navigate('/pipeline')}"><i class="bi bi-kanban"></i> New commissions start in the Asset Pipeline</button>
            </div>
            <p class="page-subtitle">Manage art commissions for freelance artists. ${this._commissions.length} total.</p>

            <div class="d-flex gap-2 mb-3">
                <select class="form-select form-select-sm" style="max-width: 180px;" @change="${this._handleStatusFilter}">
                    <option value="hide-archived" ?selected="${this._statusFilter === 'hide-archived'}">Hide archived</option>
                    <option value="">All statuses</option>
                    <option value="open" ?selected="${this._statusFilter === 'open'}">Open</option>
                    <option value="assigned" ?selected="${this._statusFilter === 'assigned'}">Assigned</option>
                    <option value="completed" ?selected="${this._statusFilter === 'completed'}">Completed</option>
                    <option value="archived" ?selected="${this._statusFilter === 'archived'}">Archived</option>
                </select>
            </div>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Slug</th>
                            <th>Type</th>
                            <th>Priority</th>
                            <th>Size</th>
                            <th>Rate</th>
                            <th>Status</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._commissions.map(
                            (c) => html`
                            <tr style="cursor: pointer;" @click="${() => this._openDetail(c)}">
                                <td><span style="color: #0d6efd; font-weight: 500;">${c.slug}</span></td>
                                <td>${c.asset_type}</td>
                                <td>${this._priorityBadge(c.priority)}</td>
                                <td>${c.miniature_size || '-'}</td>
                                <td>${formatCents(c.rate_cents)}</td>
                                <td><span class="badge status-badge ${STATUS_COLORS[c.status] || 'bg-secondary'}">${c.status}</span></td>
                                <td class="text-muted">${formatMediumDate(c.created_at)}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>

                ${this._commissions.length === 0 ? html`<p class="text-muted text-center py-3">No commissions found.</p>` : nothing}
            `
            }
        `;
    }

    _renderReferenceImages(images, editable) {
        if (!images) images = [];
        return html`
            <h6 style="font-weight: 600; font-size: 0.85rem; margin-bottom: 0.5rem;">Reference Images</h6>
            ${
                images.length > 0
                    ? html`
                <div class="ref-images-grid">
                    ${images.map(
                        (img) => html`
                        <div class="ref-thumb">
                            <media-thumb src="${img.file_url}" alt="${versionLabel(img.version)}"
                                 @media-click="${() => (this._lightboxSrc = img.file_url)}"></media-thumb>
                            <div class="ref-thumb-info">
                                <span>${versionLabel(img.version)}</span>
                                ${
                                    editable
                                        ? html`
                                    <button class="btn btn-link btn-sm p-0 text-danger" style="font-size: 0.7rem; line-height: 1;"
                                        @click="${(e) => {
                                            e.stopPropagation();
                                            this._removeReferenceImage(img.id);
                                        }}">
                                        <i class="bi bi-trash"></i>
                                    </button>
                                `
                                        : nothing
                                }
                            </div>
                        </div>
                    `,
                    )}
                </div>
            `
                    : html`<p class="text-muted small mb-2">No reference images.</p>`
            }
            ${
                editable
                    ? html`
                <upload-progress class="mt-2"
                    .api="${api}"
                    accept="image/*,video/webm"
                    label="Drop image or click to browse"
                    @upload-complete="${this._handleRefUploadComplete}">
                </upload-progress>
            `
                    : nothing
            }
        `;
    }

    _renderDetail() {
        const c = this._selected;
        if (!c) return nothing;

        return html`
            <a class="back-link" href="/commissions">
                <i class="bi bi-arrow-left"></i> Back to Commissions
            </a>

            <div class="detail-header">
                <h4>${c.slug}</h4>
                <div class="d-flex align-items-center gap-2">
                    <span class="badge ${STATUS_COLORS[c.status] || 'bg-secondary'}">${c.status}</span>
                    ${
                        c.offered_to
                            ? html`<span class="badge bg-warning text-dark">offered</span>`
                            : nothing
                    }
                    ${
                        c.status === 'open' && !c.offered_to
                            ? html`
                        <button class="btn btn-sm btn-primary" @click="${() => this._openAssignModal()}">
                            <i class="bi bi-person-plus"></i> Assign
                        </button>
                    `
                            : nothing
                    }
                    ${
                        c.offered_to
                            ? html`
                        <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._withdrawing}"
                                @click="${() => this._withdrawOffer()}">
                            ${this._withdrawing ? 'Withdrawing…' : 'Withdraw offer'}
                        </button>
                    `
                            : nothing
                    }
                </div>
            </div>

            <div class="detail-layout">
                <div class="detail-main">
                    <div class="detail-section">
                        <div class="row mb-2">
                            <div class="col-4">
                                <span class="text-muted small">Type</span>
                                <div class="fw-medium">${c.asset_type}</div>
                            </div>
                            <div class="col-4">
                                <span class="text-muted small">Priority</span>
                                <div class="fw-medium">${this._priorityBadge(c.priority)}</div>
                            </div>
                            <div class="col-4">
                                <span class="text-muted small">Rate</span>
                                <div class="fw-medium">${formatCents(c.rate_cents)}</div>
                            </div>
                        </div>
                        ${
                            (c.asset_type === 'miniature' || c.asset_type === 'tactical-map') &&
                            c.miniature_size
                                ? html`
                            <div class="row mb-2">
                                <div class="col-6">
                                    <span class="text-muted small">Size</span>
                                    <div class="fw-medium">${sizeLabel(c.asset_type, c.miniature_size)}</div>
                                </div>
                                ${
                                    c.asset_type === 'miniature'
                                        ? html`
                                    <div class="col-6">
                                        <span class="text-muted small">Miniature Type</span>
                                        <div class="fw-medium">${c.miniature_type ? (c.miniature_type === 'pc' ? 'PC' : c.miniature_type === 'monster' ? 'Monster' : c.miniature_type) : html`<span class="text-muted">Not set</span>`}</div>
                                    </div>
                                `
                                        : nothing
                                }
                            </div>
                        `
                                : nothing
                        }
                        ${
                            c.assigned_to_username
                                ? html`
                            <div class="row">
                                <div class="col-6">
                                    <span class="text-muted small">Assigned To</span>
                                    <div class="fw-medium">${c.assigned_to_username}</div>
                                </div>
                            </div>
                        `
                                : nothing
                        }
                        ${
                            c.offered_to
                                ? html`
                            <div class="row">
                                <div class="col-6">
                                    <span class="text-muted small">Offered To</span>
                                    <div class="fw-medium">${c.offered_to_username || c.offered_to}</div>
                                    <div class="text-muted small">Assigned once they countersign the SOW.</div>
                                </div>
                            </div>
                        `
                                : nothing
                        }
                        <div class="row">
                            <div class="col-6">
                                <span class="text-muted small">Open to claims</span>
                                <div class="fw-medium">${c.claimable ? 'Yes' : 'No — assigned by staff'}</div>
                            </div>
                        </div>
                    </div>

                    <div class="detail-section">
                        <h6>Statement of Work</h6>
                        ${
                            c.staff_assignment
                                ? html`<p class="text-muted mb-0">Staff assignment, no SOW.</p>`
                                : c.offered_to || c.assigned_to
                                  ? html`<sow-view commission-id="${c.id}" staff
                                        @sow-signed="${() => this._loadDetail(c.id)}"></sow-view>`
                                  : html`<p class="text-muted mb-0">
                                    Assign this commission to a freelancer to draft its Statement of Work.
                                </p>`
                        }
                    </div>

                    ${
                        c.spec_id
                            ? html`<div class="detail-section">
                        <h6>Spec</h6>
                        <div>${this._specLink(c)}${c.spec_commission_count > 1 ? html` <span class="text-muted small">· ${c.spec_commission_count} commissions</span>` : nothing}</div>
                    </div>`
                            : nothing
                    }
                    <div class="detail-section">
                        <h6>Brief</h6>
                        <div class="brief-text brief-md" .innerHTML="${briefHTML(c.brief)}"></div>
                    </div>

                    <div class="detail-section">
                        <h6>Reference Documents</h6>
                        <div class="d-flex gap-3">
                            ${
                                c.style_guide
                                    ? html`
                                <a class="spec-link" href="#" @click="${(e) => {
                                    e.preventDefault();
                                    this._modal = 'style-guide';
                                }}">
                                    <i class="bi bi-palette"></i> Style Guide
                                </a>
                            `
                                    : nothing
                            }
                            ${
                                c.technical_spec
                                    ? html`
                                <a class="spec-link" href="#" @click="${(e) => {
                                    e.preventDefault();
                                    this._modal = 'technical-spec';
                                }}">
                                    <i class="bi bi-rulers"></i> Technical Spec
                                </a>
                            `
                                    : nothing
                            }
                            ${commissionTemplateLinks(c, this._config).map(
                                (link) => html`
                                    <a class="spec-link" href="${link.url}" download>
                                        <i class="bi bi-file-earmark-arrow-down"></i> ${link.label}
                                    </a>
                                `,
                            )}
                        </div>
                    </div>

                    ${
                        c.status === 'assigned'
                            ? html`
                        <div class="detail-section">
                            <a href="/staff-assignments/${c.id}" class="btn btn-outline-primary btn-sm">
                                <i class="bi bi-arrow-right"></i> View Assignment
                            </a>
                        </div>
                    `
                            : nothing
                    }

                    <div class="d-flex gap-2">
                        <button class="btn btn-outline-primary btn-sm" @click="${this._startEdit}">Edit</button>
                        ${
                            c.status !== 'archived' && c.status !== 'completed'
                                ? html`<button class="btn btn-outline-secondary btn-sm" @click="${this._archiveCommission}">Archive</button>`
                                : c.status === 'archived'
                                  ? html`<button class="btn btn-outline-secondary btn-sm" @click="${this._reopenCommission}">Reopen</button>`
                                  : ''
                        }
                    </div>
                </div>

                <div class="detail-sidebar">
                    ${this._renderReferenceImages(c.reference_images, true)}
                </div>
            </div>

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }

    _renderModal() {
        const c = this._selected;
        if (!c || !this._modal) return nothing;

        if (this._modal === 'assign') {
            return html`
                <div class="modal-overlay" @click="${(e) => {
                    if (e.target === e.currentTarget) this._modal = null;
                }}">
                    <div class="modal-panel" style="max-width: 480px;">
                        <div class="modal-header">
                            <h5>Assign ${c.slug}</h5>
                            <button class="btn-close" @click="${() => (this._modal = null)}"></button>
                        </div>
                        <div class="modal-body">
                            <label class="form-label">Assignee</label>
                            <select class="form-select"
                                .value="${this._assignFreelancerId}"
                                @change="${(e) => (this._assignFreelancerId = e.target.value)}">
                                <option value="">Select assignee…</option>
                                ${(this._freelancers || []).map(
                                    (f) => html`
                                        <option value="${f.id}" ?selected="${this._assignFreelancerId === f.id}">${freelancerLabel(f)}</option>
                                    `,
                                )}
                            </select>
                            <div class="form-text">
                                Staff are assigned directly, with no SOW. Freelancers get an offer to countersign.
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button class="btn btn-secondary btn-sm" @click="${() => (this._modal = null)}">Cancel</button>
                            <button class="btn btn-primary btn-sm"
                                ?disabled="${this._assigning || !this._assignFreelancerId}"
                                @click="${() => this._assignCommission()}">
                                ${this._assigning ? 'Assigning…' : 'Assign'}
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        const isStyleGuide = this._modal === 'style-guide';
        const title = isStyleGuide ? 'Style Guide' : 'Technical Specification';
        const content = isStyleGuide ? c.style_guide : c.technical_spec;
        const filename = isStyleGuide
            ? `${c.asset_type}-style-guide.md`
            : `${c.asset_type}-technical-spec.md`;

        if (!content) return nothing;

        return html`
            <div class="modal-overlay" @click="${(e) => {
                if (e.target === e.currentTarget) this._modal = null;
            }}">
                <div class="modal-panel">
                    <div class="modal-header">
                        <h5>${title}</h5>
                        <button class="btn-close" @click="${() => (this._modal = null)}"></button>
                    </div>
                    <div class="modal-body">
                        <div class="spec-text">${content}</div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => downloadMarkdown(filename, content)}">
                            <i class="bi bi-download"></i> Download .md
                        </button>
                        <button class="btn btn-secondary btn-sm" @click="${() => (this._modal = null)}">Close</button>
                    </div>
                </div>
            </div>
        `;
    }

    _renderEdit() {
        return html`
            <a class="back-link" href="/commissions/${this._selected.id}">
                <i class="bi bi-arrow-left"></i> Back to ${this._selected.slug}
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Edit ${this._selected.slug}</h4>
            ${this._renderSharedNotice()}
            <div class="detail-layout">
            <div class="form-panel">
                <form @submit="${this._submitEdit}">
                    <div class="mb-3">
                        <label class="form-label">Slug</label>
                        <input type="text" class="form-control"
                            .value="${this._formData.slug}"
                            @input="${(e) => (this._formData = { ...this._formData, slug: e.target.value })}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Priority ${this._specTag()}</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, priority: e.target.value })}">
                            <option value="urgent" ?selected="${this._formData.priority === 'urgent'}">Urgent</option>
                            <option value="high" ?selected="${this._formData.priority === 'high'}">High</option>
                            <option value="medium" ?selected="${this._formData.priority === 'medium'}">Medium</option>
                            <option value="low" ?selected="${this._formData.priority === 'low'}">Low</option>
                        </select>
                    </div>
                    <div class="mb-3 form-check">
                        <input class="form-check-input" type="checkbox" id="claimable"
                            .checked="${!!this._formData.claimable}"
                            @change="${(e) => (this._formData = { ...this._formData, claimable: e.target.checked })}" />
                        <label class="form-check-label" for="claimable">Open to claims</label>
                        <div class="form-text">
                            Off by default: work is offered to a specific freelancer by staff. Turn this on
                            to also list the commission for any freelancer to claim.
                        </div>
                    </div>
                    ${
                        this._selected.asset_type === 'miniature' ||
                        this._selected.asset_type === 'tactical-map'
                            ? html`
                    <div class="mb-3">
                        <label class="form-label">Size ${this._specTag()}</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, miniature_size: e.target.value })}">
                            ${
                                this._selected.asset_type === 'tactical-map'
                                    ? html`
                                <option value="S" ?selected="${this._formData.miniature_size === 'S'}">Small (12x12)</option>
                                <option value="M" ?selected="${this._formData.miniature_size === 'M'}">Medium (24x24)</option>
                                <option value="L" ?selected="${this._formData.miniature_size === 'L'}">Large (48x48)</option>
                            `
                                    : html`
                                <option value="T" ?selected="${this._formData.miniature_size === 'T'}">Tiny</option>
                                <option value="S" ?selected="${this._formData.miniature_size === 'S'}">Small</option>
                                <option value="M" ?selected="${this._formData.miniature_size === 'M'}">Medium</option>
                                <option value="L" ?selected="${this._formData.miniature_size === 'L'}">Large</option>
                                <option value="H" ?selected="${this._formData.miniature_size === 'H'}">Huge</option>
                                <option value="G" ?selected="${this._formData.miniature_size === 'G'}">Gargantuan</option>
                            `
                            }
                        </select>
                    </div>
                    `
                            : ''
                    }
                    ${
                        this._selected.asset_type === 'miniature'
                            ? html`
                    <div class="mb-3">
                        <label class="form-label">Miniature Type * ${this._specTag()}</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, miniature_type: e.target.value })}">
                            <option value="" ?selected="${!this._formData.miniature_type}">Select type…</option>
                            <option value="pc" ?selected="${this._formData.miniature_type === 'pc'}">PC</option>
                            <option value="monster" ?selected="${this._formData.miniature_type === 'monster'}">Monster</option>
                        </select>
                    </div>
                    `
                            : ''
                    }
                    <div class="mb-3">
                        <label class="form-label">Rate ($)</label>
                        <input type="number" class="form-control" step="0.01" min="0" placeholder="e.g. 100.00"
                            .value="${this._formData.rate}"
                            @input="${(e) => (this._formData = { ...this._formData, rate: e.target.value })}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Brief ${this._specTag()}</label>
                        <textarea class="form-control" rows="5"
                            .value="${this._formData.brief}"
                            @input="${(e) => (this._formData = { ...this._formData, brief: e.target.value })}"></textarea>
                    </div>
                    <div class="d-flex gap-2">
                        <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                            ${this._saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="${() => router.navigate(`/commissions/${this._selected.id}`)}">Cancel</button>
                    </div>
                </form>
            </div>

            <div class="detail-sidebar">
                ${this._renderReferenceImages(this._selected.reference_images, true)}
            </div>
            </div>
        `;
    }

    /** The spec a commission is a job on (issue #195), linked to its pipeline panel. */
    _specLink(c) {
        if (!c.spec_id) return nothing;
        return html`<a href="/pipeline?spec=${c.spec_id}">${c.spec_name || 'its spec'}</a>`;
    }

    /** Marks a field that's saved on the commission's spec. */
    _specTag() {
        const c = this._selected;
        if (!c?.spec_id) return nothing;
        return html`<span class="badge bg-light text-secondary border fw-normal" title="Saved on spec ${c.spec_name}">spec</span>`;
    }

    /** Says which fields are the spec's, and who else they change for. */
    _renderSharedNotice() {
        const c = this._selected;
        if (!c?.spec_id) return nothing;
        const others = (c.spec_commission_count || 1) - 1;
        return html`<div class="alert alert-light border small py-2">
            <i class="bi bi-diagram-3"></i>
            Brief, priority, size, miniature type and reference images belong to spec ${this._specLink(c)}${
                others > 0
                    ? html`, so saving them here changes them for its <strong>${others} other commission${others === 1 ? '' : 's'}</strong> too`
                    : ''
            }.
        </div>`;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._view === 'edit' ? this._renderEdit() : ''}
            ${this._renderModal()}
        `;
    }
}

customElements.define('commissions-view', CommissionsView);
